// AppConfigContext.js
// 提供後端 capability flags 給全局元件使用（例如 fileUploadEnabled）
// App 啟動時 fetch 一次 GET /bff/web/config，結果存入 context

import React, { createContext, useContext, useState, useEffect } from 'react';
import logger from '../utils/logger';

const DEFAULT_CONFIG = {
  fileUploadEnabled: false, // fetch 完成前預設不顯示附件功能
  mailNotifyAutoRebuild: false, // MyAgent 已完成過期自動重建（L1／L2）時為 true，已中斷不再顯示「恢復通知」
};

const AppConfigContext = createContext(DEFAULT_CONFIG);

/**
 * 提供後端 capability flags 的 Context Provider。
 *
 * @param {object} props
 * @param {React.ReactNode} props.children
 */
export function AppConfigProvider({ children }) {
  const [config, setConfig] = useState(DEFAULT_CONFIG);

  useEffect(() => {
    const apiBase = process.env.REACT_APP_API_BASE_URL;
    if (!apiBase) {
      logger.warn('AppConfig', '未設定 REACT_APP_API_BASE_URL，跳過 config fetch');
      return;
    }

    fetch(`${apiBase}/web/config`)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => {
        logger.info('AppConfig', '取得後端 config', data);
        setConfig({
          fileUploadEnabled: Boolean(data.fileUploadEnabled),
          mailNotifyAutoRebuild: Boolean(data.mailNotifyAutoRebuild),
        });
      })
      .catch((err) => {
        // fetch 失敗時維持預設值（fileUploadEnabled: false），不影響主流程
        logger.warn('AppConfig', '取得後端 config 失敗，維持預設值', { error: err.message });
      });
  }, []);

  return (
    <AppConfigContext.Provider value={config}>
      {children}
    </AppConfigContext.Provider>
  );
}

/**
 * 取得後端 capability flags 的 hook。
 *
 * @returns {{ fileUploadEnabled: boolean, mailNotifyAutoRebuild: boolean }}
 */
export function useAppConfig() {
  return useContext(AppConfigContext);
}
